import { useState, useMemo, useCallback } from "react";
import { normalizeVnPeriodKey, vnPeriodRange } from "@apv-erp/status";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { cn } from "@/lib/utils";

const PERIOD_OPTIONS = [
    { key: 'all', label: 'Tất cả' },
    { key: 'today', label: 'Hôm nay' },
    { key: 'week', label: 'Tuần này' },
    { key: 'month', label: 'Tháng này' },
    { key: 'quarter', label: 'Quý này' },
    { key: 'year', label: 'Năm nay' },
] as const;

/** Kỳ thêm, chỉ hiện trong danh sách khi trang chọn làm mặc định (vd. 30 ngày gần nhất) */
const EXTRA_PERIOD_LABELS: Record<string, string> = {
    yesterday: 'Hôm qua',
    last7days: '7 ngày gần nhất',
    last30days: '30 ngày gần nhất',
};

/** Chuẩn hoá khoá kỳ: 'thisMonth' → 'month', 'last30Days' → 'last30days'; 'all'/'custom' giữ nguyên */
function normalizePeriod(period: string): string {
    if (period === 'all' || period === 'custom') return period;
    return normalizeVnPeriodKey(period) ?? period;
}

/**
 * Khoảng ngày {from, to} ('YYYY-MM-DD', lịch Việt Nam) cho một kỳ lọc.
 * Ngày luôn tính theo giờ VN (toVnDateString), không theo UTC: 00:30 sáng ngày 24 vẫn là ngày 24.
 */
export function computeDatePeriodRange(period: string, customFrom = '', customTo = '', now: Date = new Date()): { from?: string; to?: string } {
    if (period === 'all') return {};
    if (period === 'custom') return customFrom && customTo ? { from: customFrom, to: customTo } : {};
    return vnPeriodRange(period, now) ?? {};
}

export function useDatePeriodFilter(defaultPeriod: string = 'all') {
    const [period, setPeriodRaw] = useState<string>(() => normalizePeriod(defaultPeriod));
    const [customFrom, setCustomFrom] = useState('');
    const [customTo, setCustomTo] = useState('');
    const setPeriod = useCallback((val: string) => setPeriodRaw(normalizePeriod(val)), []);

    const dateParams = useMemo(
        () => computeDatePeriodRange(period, customFrom, customTo),
        [period, customFrom, customTo],
    );

    return { period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo, dateParams };
}

interface DatePeriodFilterProps {
    period: string;
    setPeriod: (val: string) => void;
    customFrom: string;
    setCustomFrom: (val: string) => void;
    customTo: string;
    setCustomTo: (val: string) => void;
}

export function DatePeriodFilter({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo }: DatePeriodFilterProps) {
    return (
        <div className="flex items-center gap-2 flex-nowrap shrink-0">
            {/* Compact dropdown for both mobile and desktop */}
            <div className="flex items-center gap-1.5 shrink-0">
                <CalendarDays className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <ComboboxFK
                    value={period}
                    onChange={(v) => { if (v) setPeriod(v); }}
                    options={[
                        ...PERIOD_OPTIONS.map(opt => ({ id: opt.key, label: opt.label })),
                        ...(EXTRA_PERIOD_LABELS[period] ? [{ id: period, label: EXTRA_PERIOD_LABELS[period] }] : []),
                        { id: 'custom', label: 'Tùy chỉnh' },
                    ]}
                    label=""
                    hideLabel
                    placeholder="Thời gian"
                    className="space-y-0 shrink-0 [&_button]:!h-7 [&_button]:!text-xs [&_button]:!px-2.5"
                />
            </div>
            {/* Custom date range inputs - shown only when custom period selected */}
            {period === 'custom' && (
                <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2 h-7 shadow-2xs shrink-0">
                    <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                        className="h-6 text-xs border-0 bg-transparent text-slate-700 focus:outline-none w-[108px]" />
                    <span className="text-xs text-slate-400">→</span>
                    <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                        className="h-6 text-xs border-0 bg-transparent text-slate-700 focus:outline-none w-[108px]" />
                </div>
            )}
        </div>
    );
}
